import React from 'react';
import PropTypes from 'prop-types';
import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LuBell, LuCircleCheck, LuCircleX, LuInfo, LuLoaderCircle, LuTriangleAlert, LuX } from 'react-icons/lu';
import GlassIcon from './GlassIcon';

// Per toast type: icon, its glass tile, the glow behind it and the progress bar
const TONES = {
  success: {
    icon: LuCircleCheck,
    tile: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30',
    glow: 'before:tw-bg-emerald-400/25 dark:before:tw-bg-emerald-500/20',
    bar: 'tw-from-emerald-400 tw-to-teal-500',
  },
  error: {
    icon: LuCircleX,
    tile: 'tw-from-rose-400 tw-to-red-600 tw-shadow-rose-500/30',
    glow: 'before:tw-bg-rose-400/25 dark:before:tw-bg-rose-500/20',
    bar: 'tw-from-rose-400 tw-to-red-500',
  },
  warning: {
    icon: LuTriangleAlert,
    tile: 'tw-from-amber-300 tw-to-orange-500 tw-shadow-amber-500/30',
    glow: 'before:tw-bg-amber-300/30 dark:before:tw-bg-amber-500/20',
    bar: 'tw-from-amber-300 tw-to-orange-500',
  },
  info: {
    icon: LuInfo,
    tile: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30',
    glow: 'before:tw-bg-sky-400/25 dark:before:tw-bg-sky-500/20',
    bar: 'tw-from-sky-400 tw-to-blue-500',
  },
  default: {
    icon: LuBell,
    tile: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30',
    glow: 'before:tw-bg-teal-400/25 dark:before:tw-bg-teal-500/20',
    bar: 'tw-from-teal-400 tw-to-cyan-500',
  },
};

const toneOf = (type) => TONES[type] || TONES.default;

// Container: the legacy `div[class*="container"]` dark rule would paint it
const containerClass = ({ defaultClassName }) =>
  `${defaultClassName} tw-border-0 tw-bg-transparent tw-shadow-none tw-font-sans tw-antialiased`;

// Replaces the library's toast classes entirely (and with them the legacy
// `.Toastify__toast` overrides). No transform utilities here: the enter/exit
// animation moves this element.
const toastClass = ({ type }) => [
  'tw-group tw-relative tw-isolate tw-mb-3 tw-flex tw-min-h-[3.75rem] tw-cursor-pointer tw-items-center tw-overflow-hidden tw-rounded-2xl tw-p-3 tw-pr-11',
  'tw-border tw-border-white/80 tw-bg-white/95 tw-text-slate-800',
  'tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_0_0_1px_rgba(15,23,42,0.05),0_20px_44px_-18px_rgba(15,23,42,0.4)]',
  'dark:tw-border-white/10 dark:tw-bg-[#262a34]/95 dark:tw-text-slate-100',
  'dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_20px_44px_-18px_rgba(0,0,0,0.85)]',
  // Soft glow in the toast's colour behind the icon
  "before:tw-pointer-events-none before:tw-absolute before:-tw-left-8 before:-tw-top-10 before:-tw-z-10 before:tw-h-28 before:tw-w-28 before:tw-rounded-full before:tw-blur-2xl before:tw-content-['']",
  toneOf(type).glow,
].join(' ');

// The message is the body's last child (the icon slot comes first)
const bodyClass = () => [
  'tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3 tw-text-sm tw-font-medium tw-leading-snug tw-text-slate-800 dark:tw-text-slate-100',
  '[&>div:last-child]:tw-min-w-0 [&>div:last-child]:tw-flex-1 [&>div:last-child]:tw-break-words',
].join(' ');

const progressClass = ({ type, defaultClassName }) =>
  `${defaultClassName} tw-bg-gradient-to-r tw-opacity-90 ${toneOf(type).bar}`;

const ToastIcon = ({ type, isLoading }) => {
  if (isLoading) {
    return (
      <GlassIcon gradient={TONES.default.tile} size="sm">
        <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
      </GlassIcon>
    );
  }
  const { icon: Icon, tile } = toneOf(type);
  return (
    <GlassIcon gradient={tile} size="sm">
      <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
    </GlassIcon>
  );
};

ToastIcon.propTypes = {
  type: PropTypes.string,
  isLoading: PropTypes.bool,
};

const CloseButton = ({ closeToast }) => (
  <button
    type="button"
    onClick={closeToast}
    aria-label="Bildirimi kapat"
    className="tw-absolute tw-right-2 tw-top-2 tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-500 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
  >
    <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
  </button>
);

CloseButton.propTypes = {
  closeToast: PropTypes.func,
};

/**
 * The app's single toast container, in the glass style. Pages just call
 * `toast.success(...)` etc. from react-toastify; they must not render a
 * ToastContainer of their own (each container would show every toast again).
 * Offsets, width and the library's inner wrappers: "Toasts" in
 * src/styles/tailwind.css.
 */
const Toaster = () => (
  <ToastContainer
    position="top-right"
    autoClose={3500}
    newestOnTop
    closeOnClick
    pauseOnHover
    pauseOnFocusLoss
    draggable
    transition={Slide}
    className={containerClass}
    toastClassName={toastClass}
    bodyClassName={bodyClass}
    progressClassName={progressClass}
    icon={ToastIcon}
    closeButton={CloseButton}
  />
);

export default Toaster;
